import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faQuoteLeft, 
  faStar, 
  faChevronLeft, 
  faChevronRight,
  faCheckCircle 
} from "@fortawesome/free-solid-svg-icons";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";

import { imageUrls } from "../lib/supabase";
import { getMockGoogleReviews, fetchGoogleReviews } from "../lib/googleReviews";

const TestimonialCard = ({ 
  name, 
  text, 
  rating = 5, 
  time = null, 
  profilePhoto = null
}) => {
  const authorImage = profilePhoto || imageUrls.person1();
  
  return (
    <div className="testimonial-card google-review">
      <div className="testimonial-header">
        <div className="testimonial-icon">
          <FontAwesomeIcon icon={faQuoteLeft} />
        </div>
        <div className="review-source">
          <FontAwesomeIcon icon={faGoogle} className="google-icon" />
          <span className="source-text">Google Review</span>
          <FontAwesomeIcon icon={faCheckCircle} className="verified-icon" />
        </div>
      </div>
      
      <div className="testimonial-content">
        <p>{text}</p>
      </div>
      
      <div className="testimonial-rating">
        {[...Array(5)].map((_, i) => (
          <FontAwesomeIcon 
            key={i} 
            icon={faStar} 
            className={`rating-star ${i < rating ? 'filled' : 'empty'}`} 
          />
        ))}
        <span className="rating-number">({rating}/5)</span>
      </div>
      
      <div className="testimonial-author">
        <img src={authorImage} alt={name} className="author-img" />
        <div className="author-info">
          <h4>{name}</h4>
          {time && <span className="review-time">{time}</span>}
        </div>
      </div>
    </div>
  );
};

const SlidingCarousel = ({ reviews, googlePlaceInfo }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const reviewsPerSlide = 3;
  const totalSlides = Math.ceil(reviews.length / reviewsPerSlide);
  
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };
  
  const getCurrentReviews = () => {
    const start = currentSlide * reviewsPerSlide;
    const end = start + reviewsPerSlide;
    return reviews.slice(start, end);
  };
  
  if (reviews.length === 0) return null;
  
  return (
    <div className="sliding-carousel">
      <div className="carousel-header">
      {googlePlaceInfo && (
            <div className="google-rating-summary">
              <div className="rating-display">
                <FontAwesomeIcon icon={faGoogle} className="google-icon" />
                <span className="rating-score">{googlePlaceInfo.rating}</span>
                <div className="rating-stars">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon 
                      key={i} 
                      icon={faStar} 
                      className={`rating-star ${i < Math.floor(googlePlaceInfo.rating) ? 'filled' : 'empty'}`} 
                    />
                  ))}
                </div>
                <span className="review-count">({googlePlaceInfo.totalReviews} recenzii)</span>
              </div>
            </div>
          )}
        
        <div className="carousel-navigation">
          <button 
            className="carousel-nav-btn prev" 
            onClick={prevSlide}
            disabled={totalSlides <= 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          <span className="slide-indicator">
            {currentSlide + 1} / {totalSlides}
          </span>
          
          <button 
            className="carousel-nav-btn next" 
            onClick={nextSlide}
            disabled={totalSlides <= 1}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      
      <div className="carousel-slides">
        <div className="row g-4">
          {getCurrentReviews().map((review, index) => (
            <div className="col-lg-4 col-md-6" key={`${currentSlide}-${index}`}>
              <TestimonialCard {...review} />
            </div>
          ))}
        </div>
      </div>
      
      <div className="carousel-dots">
        {Array.from({ length: totalSlides }, (_, index) => (
          <button
            key={index}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => setCurrentSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

const Testimonials = () => {
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googlePlaceInfo, setGooglePlaceInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGoogleReviews = async () => {
      try {
        setLoading(true);
        // Try to use real Google Reviews API, fallback to mock data
        let reviewsData;
        try {
          reviewsData = await fetchGoogleReviews();
          // If no reviews from API, fallback to mock data
          if (!reviewsData.reviews || reviewsData.reviews.length === 0) {
            console.warn('No real reviews found, using mock data');
            reviewsData = getMockGoogleReviews();
          }
        } catch (error) {
          console.warn('Google Reviews API failed, using mock data:', error);
          reviewsData = getMockGoogleReviews();
        }
        
        // Filter and sort to get top 10 highest rated reviews
        const topReviews = reviewsData.reviews
          .filter(review => review.rating >= 4) // Only 4+ star reviews
          .sort((a, b) => b.rating - a.rating) // Sort by highest rating first
          .slice(0, 10); // Limit to 10 reviews
        
        setGoogleReviews(topReviews);
        setGooglePlaceInfo({
          rating: reviewsData.rating,
          totalReviews: reviewsData.totalReviews,
          place: reviewsData.place
        });
      } catch (error) {
        console.error('Failed to load Google reviews:', error);
        setGoogleReviews([]);
      } finally {
        setLoading(false);
      }
    };

    loadGoogleReviews();
  }, []);

  if (loading) {
    return (
      <section className="section testimonials-section" id="testimonials">
        <div className="container">
          <div className="text-center">
            <h2 className="section-title">Ce spun pacienții noștri</h2>
            <p>Se încarcă recenziile...</p>
          </div>
        </div>
      </section>
    );
  }

  if (googleReviews.length === 0) {
    return (
      <section className="section testimonials-section" id="testimonials">
        <div className="container">
          <div className="text-center">
            <h2 className="section-title">Ce spun pacienții noștri</h2>
            <p>Nu am putut încărca recenziile în acest moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="section testimonials-section"
      id="testimonials"
    >
      <div className="container">
        <div className="testimonials-header">
          <h2 className="section-title">Ce spun pacienții noștri</h2>
        </div>

        {/* Sliding Carousel with all reviews */}
        <SlidingCarousel reviews={googleReviews} googlePlaceInfo={googlePlaceInfo} />

        {/* Call to Action */}
        <div className="testimonials-cta mt-5 text-center">
          <div className="cta-content-wrapper">
            <h3 className="cta-title">Ai fost pacientul nostru?</h3>
            <p className="cta-subtitle">Experiența ta contează! Ajută-ne să îmbunătățim serviciile noastre.</p>
            <a 
              href="https://www.google.com/search?sca_esv=4da7261d6af0beeb&sxsrf=AE3TifM7OfoNbYKePPgUaU8Ri9XW6VcBXg:1751464577042&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E-atG76jhYm0EdRk6BIInvyHkJ54FHsKQ6WVasLRNVHvRanLcvwQQ78WulUr4V6myC84qwxfzYCnHeUfUEnlbthuaamH-KFAGSwdcq6dsv36SFl2KQ%3D%3D&q=Ali+Cranta+Medical+Center+Recenzii&sa=X&ved=2ahUKEwjF7s3_qZ6OAxVQJRAIHZYXEMoQ0bkNegQIIxAE&biw=2560&bih=1191&dpr=1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-google-review"
            >
              <FontAwesomeIcon icon={faGoogle} className="me-2" />
              Lasă o recenzie pe Google
            </a>
            <div className="cta-rating-info">
              <div className="rating-stars-display">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon key={i} icon={faStar} className="star-icon" />
                ))}
              </div>
              <span className="rating-text">Evaluează-ne cu 5 stele!</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
